import React, { useState } from 'react';
import { CompetitionType } from 'src/domains/layouts/webview/components/promotedSports/PromotedEventsState.state';
import { observer } from 'src/utils/mobx-react';
import { EventRowMobxWrapper } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowMobx';
import { EventRow } from 'src/domains/sportsbook/webview/components/eventRow/EventRow';
import { EventRowNFL } from 'src/domains/sportsbook/shared/Components';
import { EventRowMLB } from 'src/domains/sportsbook/webview/components/eventsList/eventRowMLB/EventRowMLB';
import { EventRowNBA } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNBA/EventRowNBA';
import { EventRowNHL } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNHL/EventRowNHL';
import { makeObservable, observable } from 'mobx';
import {
    getMarketAndSelections,
    PossibleMarketAndSelectionInListType,
} from 'src/domains/sportsbook/state/eventsListState/eventsGroup/getMarketAndSelections';
import { SELECTIONS_LABELS } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupView';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventGroupHeaderMLB } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderMLB';
import { EventGroupHeaderNBA } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNBA';
import { EventGroupHeaderNFL } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNFL';
import { EventGroupHeaderNHL } from 'src/domains/sportsbook/webview/components/eventsList/EventGroupHeaderNHL';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { checkCompetition } from 'src/domains/sportsbook/utils/checkCompetition';
import { CompetitionHeader } from 'src/domains/sportsbook/webview/components/competitionHeader/CompetitionHeader';
import { CompetitionWrapper } from './PromotedSportSection.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';

interface CompetitionDataPropsType {
    competition: CompetitionType;
    sportSlug: string;
}

const renderCompetitionView = (
    competitionId: CompetitionId,
    event: EventListGroupEventItemType,
    sportSlug: string,
    possibleMarketAndSelectionInList: PossibleMarketAndSelectionInListType
): JSX.Element => {
    const competitionName = competitionId.getModel()?.name ?? '';
    const mlbOrMajorCompetition = checkCompetition(competitionName, ['MLB', 'Major']);
    const { id2, id, template } = event;

    if (mlbOrMajorCompetition === true && template.startsWith('two-participant')) {
        return (
            <EventRowMLB
                key={`event_${id}`}
                eventId={id2}
                smallEvent={event}
            />
        );
    } else if (checkCompetition(competitionName, ['NHL']) === true && template.startsWith('two-participant')) {
        return (
            <EventRowNHL
                key={`event_${id}`}
                eventId={id2}
                smallEvent={event}
            />
        );
    } else if (checkCompetition(competitionName, ['NBA']) === true && template.startsWith('two-participant')) {
        return (
            <EventRowNBA
                key={`event_${id}`}
                eventId={id2}
                smallEvent={event}
            />
        );
    } else if (competitionName === 'US NFL' || competitionName === 'NFL Matches') {
        return (
            <EventRowNFL
                key={`event_${id}`}
                eventId={id2}
                smallEvent={event}
            />
        );
    } else if (sportSlug === 'football') {
        return (
            <EventRowMobxWrapper
                key={`event_${id}`}
                smallEvent={event}
                competitionId={competitionId}
            />
        );
    } else {
        return (
            <EventRow
                key={`event_${event.id}`}
                sport={sportSlug}
                smallEvent={event}
                possibleMarketAndSelectionInList={possibleMarketAndSelectionInList}
                filterMarketsByPopularTag={false}
            />
        );
    }
};

interface HeaderType {
    competition: CompetitionType;
    isOpenedCompetition: boolean;
    sportSlug: string;
    setIsOpened: () => void;
    smallEventsArray: Array<EventListGroupEventItemType>;
}

const Header = observer(
    'Header',
    ({ competition, isOpenedCompetition, sportSlug, setIsOpened, smallEventsArray }: HeaderType): JSX.Element => {
        const common = useCommon();
        const languagesState = LanguagesState.get(common);

        const competitionName = competition.competition_id.getModel()?.name ?? '';
        const possibleMarketAndSelectionInList = getMarketAndSelections(smallEventsArray);
        const selectionsLabels = SELECTIONS_LABELS(languagesState.getTranslation);
        const selections = possibleMarketAndSelectionInList.headers.map(
            (x) => selectionsLabels[`${sportSlug}:${x}`] ?? selectionsLabels[x] ?? x
        );
        const mlbOrMajorCompetition = checkCompetition(competitionName, ['MLB', 'Major']);
        const areEventsTwoParticipantsOnly = smallEventsArray.every((smallEvent) =>
            smallEvent.template.startsWith('two-participant')
        );

        if (mlbOrMajorCompetition === true && areEventsTwoParticipantsOnly === true) {
            return (
                <EventGroupHeaderMLB
                    key={`sport_competition_${competition.competition_id.toOldId()}`}
                    label={competitionName}
                    onToggle={setIsOpened}
                    open={isOpenedCompetition}
                    dataTest='header-competitionLevel'
                />
            );
        } else if (checkCompetition(competitionName, ['NHL']) === true && areEventsTwoParticipantsOnly === true) {
            return (
                <EventGroupHeaderNHL
                    key={`sport_competition_${competition.competition_id.toOldId()}`}
                    label={competitionName}
                    onToggle={setIsOpened}
                    open={isOpenedCompetition}
                    dataTest='header-competitionLevel'
                />
            );
        } else if (checkCompetition(competitionName, ['NBA']) === true && areEventsTwoParticipantsOnly === true) {
            return (
                <EventGroupHeaderNBA
                    key={`sport_competition_${competition.competition_id.toOldId()}`}
                    label={competitionName}
                    onToggle={setIsOpened}
                    open={isOpenedCompetition}
                    dataTest='header-competitionLevel'
                />
            );
        } else if (competitionName === 'US NFL' || competitionName === 'NFL Matches') {
            return (
                <EventGroupHeaderNFL
                    dataTest='header-competitionLevel'
                    key={`sport_competition_${competition.competition_id.toOldId()}`}
                    label={competitionName}
                    onToggle={setIsOpened}
                    open={isOpenedCompetition}
                />
            );
        } else {
            return (
                <CompetitionHeader
                    selections={selections}
                    isOutrightEvent={false}
                    label={competitionName}
                    isOpen={isOpenedCompetition}
                    onToggle={setIsOpened}
                />
            );
        }
    }
);

class State {
    @observable public isOpenedCompetition: boolean = true;

    public constructor() {
        makeObservable(this);
    }

    public setIsOpened = (): void => {
        this.isOpenedCompetition = !this.isOpenedCompetition;
    };
}

export const CompetitionData = observer('CompetitionData', ({ competition, sportSlug }: CompetitionDataPropsType) => {
    const common = useCommon();
    const [state] = useState(() => new State());

    const configState = ConfigComponents.get(common).config;
    const smallEventsArray = competition.events_list
        .map((event) => event.smallEvent)
        .filter((event) => {
            const eventModel = event.id2.getEventModel();
            if (eventModel === null || eventModel.display === false || eventModel.timeSettingsStarted) {
                return false;
            }

            if (eventModel.sport === 'horseracing' || eventModel.sport === 'greyhoundracing') {
                return eventModel.template === 'outrights' || eventModel.antePost;
            }
            return true;
        });
    const possibleMarketAndSelectionInList = getMarketAndSelections(smallEventsArray);
    const eventsSorted = competition.events_list.sort((a, b) => a.display_order - b.display_order);

    return eventsSorted.length > 0 ? (
        <React.Fragment key={competition.id}>
            <Header
                competition={competition}
                sportSlug={sportSlug}
                isOpenedCompetition={state.isOpenedCompetition}
                setIsOpened={state.setIsOpened}
                smallEventsArray={smallEventsArray}
            />
            {state.isOpenedCompetition ? (
                <CompetitionWrapper variant={configState.componentVariants.sportsbookVariant}>
                    {eventsSorted.map((eventModel) => {
                        const smallEvent = eventModel.smallEvent;

                        return renderCompetitionView(
                            competition.competition_id,
                            smallEvent,
                            sportSlug,
                            possibleMarketAndSelectionInList
                        );
                    })}
                </CompetitionWrapper>
            ) : null}
        </React.Fragment>
    ) : null;
});
